/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useMemo, useState } from 'react';
import { Avatar, makeStyles, Table, TableBody, TableRow, TableCell, TableContainer, TableHead, IconButton, Theme } from '@material-ui/core';
import { EmailOutlined } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { StyledText } from '../StyledText';
import Button from '../Button';
import { ConnectorData, EntReferralOption, MatchListType, ReferralStatus } from '../../types/types';
import ConnectorMessageDialog from './ConnectorMessageDialog';
import api from '../../api/api';
import { useAlert, useEntContext, useUser } from '../../contexts';
import MatchCountInfoIcon from '../MatchCountInfo';


const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0
  },
  nameAndPhoto: {
    display: 'inline-flex',
    cursor: 'pointer',
    alignItems: 'center',
    '&:hover': {
      color: theme.palette.secondary.main
    }
  },
  iconButton: {
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    width: 40,
    height: 40,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark
    }
  },
  centerItems: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '16px'
  }
}));

const useTableStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5)
    }
  }
}));

interface ChildTableProps {
  investorName: string
  connectorList: ConnectorData[]
  viewProfile: (d: ConnectorData) => void,
  handleOpenMessageDialog: (d: ConnectorData) => void
}

const RequestableConnectorTable: React.FC<Omit<ChildTableProps, 'investorName'>> = ({
  connectorList,
  viewProfile,
  handleOpenMessageDialog
}) => {
  const styles = useStyles();
  const tableStyles = useTableStyles();

  return (
    <TableContainer classes={tableStyles}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <StyledText variant='h5' color='primary'>Connector</StyledText>
            </TableCell>
            <TableCell>
              <StyledText variant='h5' color='primary'>Investors in network</StyledText>
            </TableCell>
            <TableCell>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledText variant='h5' color='primary' style={{ marginRight: 10 }}>Matched Investors</StyledText>
                <div style={{ width: 18, height: 18 }}>
                  <MatchCountInfoIcon />
                </div>
              </div>
            </TableCell>
            <TableCell>
              <StyledText variant='h5' color='primary'>{' '}</StyledText>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {connectorList.map((i: ConnectorData) => (
            <TableRow key={`connector-table-row-${i.id}`}>
              <TableCell>
                <div className={styles.nameAndPhoto} onClick={() => { viewProfile(i); }}>
                  <Avatar src={i.profileImage} />
                  <div style={{ padding: '0px 20px' }}>
                    <StyledText variant='subtitle3'>{`${i.firstName} ${i.lastName}`}</StyledText>
                    <StyledText>{i.companyName}</StyledText>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <StyledText>{i.investorCount}</StyledText>
              </TableCell>
              <TableCell>
                <StyledText>{i.matchCount}</StyledText>
              </TableCell>
              <TableCell>
                <div className={styles.centerItems}>
                  <Button variant='text' onClick={() => { viewProfile(i); }}>View profile</Button>
                  <IconButton
                    className={styles.iconButton}
                    onClick={() => handleOpenMessageDialog(i)}
                  >
                    <EmailOutlined />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RequestedListTable: React.FC<ChildTableProps> = ({
  investorName,
  connectorList,
  viewProfile,
  handleOpenMessageDialog
}) => {
  const styles = useStyles();
  const tableStyles = useTableStyles();

  const [requestedConnectors, otherConnectors] = useMemo(() => {
    const reqList = connectorList.filter((c) => c.referralData && c.referralData.status === ReferralStatus.Requested);
    const otherList = connectorList.filter((c) => {
      const refData = c.referralData;
      if (refData) {
        return refData.status === undefined;
      }
      return true;
    });
    return [reqList, otherList];
  }, [connectorList]);
  return (
    <div>
      <StyledText style={{ margin: 15 }} variant='h4'>Connectors Requested</StyledText>
      <StyledText style={{ margin: 15 }}>{`You've asked these connectors for referrals to ${investorName}`}</StyledText>
      <TableContainer classes={tableStyles}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <StyledText variant='h5' color='primary'>Connector</StyledText>
              </TableCell>
              <TableCell>
                <StyledText variant='h5' color='primary'>Your message</StyledText>
              </TableCell>
              <TableCell>
                <StyledText variant='h5' color='primary'>{' '}</StyledText>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requestedConnectors.map((i: ConnectorData) => (
              <TableRow key={`connector-table-row-${i.id}`}>
                <TableCell>
                  <div className={styles.nameAndPhoto} onClick={() => { viewProfile(i); }}>
                    <Avatar src={i.profileImage} />
                    <div style={{ padding: '0px 20px' }}>
                      <StyledText variant='subtitle3'>{`${i.firstName} ${i.lastName}`}</StyledText>
                      <StyledText>{i.companyName}</StyledText>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <StyledText>{i.referralData ? i.referralData.entMessage || '' : ''}</StyledText>
                </TableCell>
                <TableCell>
                  <div className={styles.centerItems}>
                    <Button variant='text' onClick={() => { viewProfile(i); }}>View profile</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <br />
      {otherConnectors.length > 0 ? (
        <>
          <StyledText style={{ margin: 15 }} variant='h4'>Other Connectors</StyledText>
          <StyledText style={{ margin: 15 }}>{`You can request referrals from other connectors until one of them refers you to ${investorName}`}</StyledText>
          <RequestableConnectorTable
            connectorList={otherConnectors}
            viewProfile={viewProfile}
            handleOpenMessageDialog={handleOpenMessageDialog}
          />
        </>
      ) : null }
    </div>
  );
};

const ReferredListTable: React.FC<ChildTableProps> = ({
  connectorList,
  viewProfile
}) => {
  const styles = useStyles();

  const [connector, status] = useMemo(() => {
    const conn = connectorList.find((c) => {
      const { referralData } = c;
      if (referralData && referralData.status !== ReferralStatus.Requested) {
        return c;
      }
      return false;
    });
    return [conn, conn?.referralData?.status || undefined];
  }, [connectorList]);

  if (!connector || !status) {
    return <></>;
  }

  return (
    <div>
      <StyledText style={{ margin: 15 }} variant='h4'>Referred By</StyledText>
      <div className={styles.nameAndPhoto} onClick={() => { viewProfile(connector); }}>
        <Avatar src={connector.profileImage} />
        <div style={{ padding: '0px 20px' }}>
          <StyledText variant='subtitle3'>{`${connector.firstName} ${connector.lastName}`}</StyledText>
          <StyledText>{connector.companyName}</StyledText>
        </div>
      </div>
    </div>
  );
};

const RecommendedListTable: React.FC<ChildTableProps> = ({
  investorName,
  connectorList,
  handleOpenMessageDialog,
  viewProfile
// eslint-disable-next-line arrow-body-style
}) => {
  return (
    <div>
      <StyledText style={{ margin: 15 }} variant='h4'>Connectors</StyledText>
      <StyledText style={{ margin: 15 }}>{`Pick a connector to introduce you to ${investorName}`}</StyledText>
      <RequestableConnectorTable
        connectorList={connectorList}
        viewProfile={viewProfile}
        handleOpenMessageDialog={handleOpenMessageDialog}
      />
    </div>
  );
};

interface ChildTableRouterProps extends ChildTableProps {
  listType: MatchListType
}

const ChildListTable: React.FC<ChildTableRouterProps> = ({
  investorName,
  connectorList,
  viewProfile,
  handleOpenMessageDialog,
  listType
}) => {
  switch (listType) {
    case MatchListType.requested:
      return (
        <RequestedListTable
          connectorList={connectorList}
          investorName={investorName}
          viewProfile={viewProfile}
          handleOpenMessageDialog={handleOpenMessageDialog}
        />
      );
    case MatchListType.sent:
      return (
        <ReferredListTable
          connectorList={connectorList}
          investorName={investorName}
          viewProfile={viewProfile}
          handleOpenMessageDialog={handleOpenMessageDialog}
        />
      );
    default:
      return (
        <RecommendedListTable
          connectorList={connectorList}
          investorName={investorName}
          viewProfile={viewProfile}
          handleOpenMessageDialog={handleOpenMessageDialog}
        />
      );
  }
};

interface ConnectorTableProps {
  investorName: string
  investorId: number
  connectorList: ConnectorData[]
  listType: MatchListType
  refreshHandler: () => void
}


const ConnectorTable: React.FC<ConnectorTableProps> = ({
  investorName,
  investorId,
  connectorList,
  listType,
  refreshHandler
}) => {
  const styles = useStyles();
  const history = useHistory();
  const { getAccessToken } = useUser();
  const { profile } = useEntContext();
  const [currentConnectorData, setCurrentConnectorData] = useState<ConnectorData>();
  const { postNewAlert } = useAlert();

  const handleOpenMessageDialog = (data: ConnectorData) => {
    setCurrentConnectorData(data);
  };

  const handleMessageDialogSubmit = async (values: any) => {
    try {
      if (profile === undefined || profile === null) throw new Error('Profile missing for Entrepreneur');
      if (!currentConnectorData) return;

      if (values.isCorporateReferralChecked) {
        await api.sendPartnerReferrals(await getAccessToken());
      }

      const { investors } = values;
      await api.sendAskReferrals(await getAccessToken(), {
        entMessage: values.message,
        connectorProfileId: currentConnectorData.id,
        investorsRequested: investors.map((i: EntReferralOption) => i.investorProfileId)
      });
      refreshHandler();
    } catch (e: unknown) {
      console.error(e);
      postNewAlert('An error occurred while sending the referrals.  Please try again later.');
    } finally {
      setCurrentConnectorData(undefined);
    }
  };

  const viewProfile = (i: ConnectorData) => {
    history.push(`/connector/${i.id}?from=${investorId}`, { from: window.location.pathname, investorCount: i.investorCount, matchCount: i.matchCount });
  };

  return (
    <div className={styles.root}>
      {currentConnectorData !== undefined && profile
        && (
        <ConnectorMessageDialog
          open={currentConnectorData !== undefined}
          connectorData={currentConnectorData}
          handleClose={() => setCurrentConnectorData(undefined)}
          preselectedReferral={investorId}
          handleSubmit={handleMessageDialogSubmit}
        />
        )}
      <ChildListTable
        connectorList={connectorList}
        investorName={investorName}
        viewProfile={viewProfile}
        handleOpenMessageDialog={handleOpenMessageDialog}
        listType={listType}
      />
    </div>
  );
};

export default ConnectorTable;
